import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.#pauseOtherMedia, { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    const existingVideo = this.querySelector(':scope > video');

    if (existingVideo instanceof HTMLVideoElement) {
      this.setAttribute('data-media-loaded', 'true');
      this.#bindVideoPlaybackEvents(existingVideo);
      this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');
      if (this.hasAttribute('autoplay')) {
        // play() returns a promise; we don't await it because connectedCallback should stay sync,
        // and play/pause events plus #syncPlayPauseHintFromVideo() already handle UI state.
        void existingVideo.play();
      }
      this.#syncPlayPauseHintFromVideo();
    } else if (this.hasAttribute('autoplay')) {
      const video = this.querySelector('video');
      if (video instanceof HTMLVideoElement) {
        this.#bindVideoPlaybackEvents(video);
        this.#syncPlayPauseHintFromVideo();
      } else if (this.querySelector('iframe[data-video-type]')) {
        this.isPlaying = true;
        this.updatePlayPauseHint(true);
      }
    }
  }

  /**
   * @param {Event} event
   */
  #pauseOtherMedia = (event) => {
    if (event.target === this) return;

    this.pauseMedia();
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    if (this.getAttribute('data-media-loaded')) {
      this.playMedia();
      return;
    }

    this.loadContent(true);
    this.#syncPlayPauseHintFromVideo();
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    const existingVideo = this.querySelector(':scope > video');
    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!existingVideo && !content) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    this.setAttribute('data-media-loaded', 'true');
    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    const media =
      existingVideo instanceof HTMLVideoElement
        ? existingVideo
        : content instanceof HTMLVideoElement
          ? content
          : content instanceof HTMLIFrameElement
            ? content
            : null;

    if (!existingVideo && content) {
      this.appendChild(content);
    }

    if (focus && media instanceof HTMLElement && !(media instanceof HTMLIFrameElement)) {
      media.focus({ preventScroll: true });
    }

    if (media instanceof HTMLVideoElement) {
      this.#bindVideoPlaybackEvents(media);

      if (media.getAttribute('autoplay')) {
        void media.play();
      }
    }

    this.#syncPlayPauseHintFromVideo();
  }

  /**
   * @param {HTMLVideoElement} video
   */
  #bindVideoPlaybackEvents(video) {
    if (video.dataset.playbackBound === 'true') return;

    video.dataset.playbackBound = 'true';

    const signal = this.#abortController.signal;

    video.addEventListener(
      'play',
      () => {
        this.isPlaying = true;
        this.updatePlayPauseHint(true);
      },
      { signal }
    );

    video.addEventListener(
      'pause',
      () => {
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
      },
      { signal }
    );
  }

  #syncPlayPauseHintFromVideo() {
    const video = this.querySelector(':scope > video');

    if (!(video instanceof HTMLVideoElement)) {
      this.isPlaying = true;
      this.updatePlayPauseHint(true);
      return;
    }

    this.isPlaying = !video.paused;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    const video = this.querySelector('video');
    if (video instanceof HTMLVideoElement) {
      if (video.paused) {
        this.playMedia();
      } else {
        this.pauseMedia();
      }
      return;
    }

    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    if (this.getAttribute('data-media-loaded') || this.querySelector(':scope > video')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
